import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { Empty, Flex, Spin, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { connect, useSelector } from "react-redux"

import { DashboardFiltersBlock } from "../../../Components/Filters/DashboardFiltersBlock/DashboardFiltersBlock"
import DropdownBlockCheckbox from "../../../Components/Filters/DropdownBlockCheckbox"
import {
  countries,
  countryCodesByCountry,
  countryFlagsByCountryCode
} from "../../../Components/Filters/filterOptionsData"
import Private from "../../../Components/Private"
import SelectComponent from "../../../Components/Select"
import TableComponent from "../../../Components/Table"
import Images from "../../../Images"
import { getDates } from "../../../Utils/utils"
import PercentageBar from "../components/ByCountryBlock/PercentageBar"
import HealthMobilePagination from "../components/HealthMobilePagination"
import HealthSubpageHeader from "../components/HealthSubpageHeader"
import HealthSubpageLayout from "../components/HealthSubpageLayout"
import {
  formatAmount,
  formatCount,
  getColumnsByCountry,
  getRowPercentage
} from "../components/Table/columns"
import { useHealthFilters, useHealthSubpage, useTableSettings } from "../hooks"
import Creators from "../reducer"

import "../components/ByCountryBlock/styles.less"

const { Text } = Typography

const pageSize = 50

const BreakdownByCountryPage = ({
  userInfo,
  filters,
  selectCountries,
  dataByCountryFetching,
  dataByCountry,
  theme,
  isAdmin,
  admin_selected_business_account_id,
  ...props
}) => {
  const { t } = useTranslation()
  const isMobile = useSelector((state) => state.nav.isMobile)
  const businessId = isAdmin
    ? admin_selected_business_account_id
    : userInfo?.current_business_account_id
  const { count, result } = dataByCountry
  const [mobileColumn, setMobileColumn] = useState("transactions")
  const { dataSource } = useSelector((state) => state.health)

  // Custom hooks for common logic
  const { pageNumber, ordering, handleChangeTable, setPageNumber } = useHealthSubpage(pageSize)
  const { handleChangeFilter } = useHealthFilters(props.changeFilters)

  // Data fetching
  const helpRef = useRef({ firstLoading: true })

  const countryCodesFilter = useMemo(() => {
    if (selectCountries === "all" || !selectCountries || !selectCountries.length) {
      return null
    }
    return selectCountries
      ?.map((countryName) => countryCodesByCountry[countryName])
      ?.filter((code) => code)
      ?.join(",")
  }, [selectCountries])

  useEffect(() => {
    if (filters?.period) {
      const period_query = getDates(filters.period)
      props.getDataByCountry({
        page: pageNumber,
        page_size: pageSize,
        ordering,
        business_account: businessId,
        country: countryCodesFilter,
        source: dataSource ?? "stripe",
        ...period_query
      })

      if (helpRef.current.firstLoading) {
        helpRef.current.firstLoading = false
      }
    }
  }, [
    filters?.period,
    businessId,
    ordering,
    pageNumber,
    pageSize,
    props.getDataByCountry,
    countryCodesFilter,
    dataSource
  ])

  // Memoized data
  const columns = useMemo(() => getColumnsByCountry({ t, withSort: true }), [t])

  const tableSettings = useTableSettings(count, pageNumber, pageSize)

  const mobileColumnOptions = useMemo(
    () => [
      { label: t("Transactions"), value: "transactions" },
      { label: t("Chargebacks"), value: "chargebacks" },
      { label: t("Alerts"), value: "alerts" },
      { label: t("Chargeback rate"), value: "chargeback_rate" },
      { label: t("Prevention rate"), value: "prevention_rate" }
    ],
    [t]
  )

  const selectedMobileColumnLabel =
    mobileColumnOptions.find((option) => option.value === mobileColumn)?.label || t("Column")

  // Custom row component with percentage bar
  const CustomRow = (props) => {
    const percentage = props["data-percentage"] || 0
    return (
      <PercentageBar percentage={percentage} theme={theme}>
        <tr {...props} />
      </PercentageBar>
    )
  }

  const tableComponents = useMemo(
    () => ({
      body: {
        row: CustomRow
      }
    }),
    [result, theme]
  )

  const handleChangeFilterCountries = useCallback((newFilters) => {
    props.changeCounties(newFilters?.["country"])
    setPageNumber(1)
  }, [])

  const renderMobileValueCell = useCallback(
    (record) => {
      switch (mobileColumn) {
        case "transactions":
          return (
            <Flex gap={2} vertical={true} align="flex-end">
              <Text strong>{formatCount(record?.transactions_count)}</Text>
              <Text type="secondary" strong>
                <Private>{formatAmount(record?.transactions_amount)}</Private>
              </Text>
            </Flex>
          )
        case "chargebacks":
          return (
            <Flex gap={2} vertical={true} align="flex-end">
              <Text strong>{formatCount(record?.chargebacks_count)}</Text>
              <Text type="secondary" strong>
                <Private>{formatAmount(record?.chargebacks_amount)}</Private>
              </Text>
            </Flex>
          )
        case "alerts":
          return (
            <Flex gap={2} vertical={true} align="flex-end">
              <Text strong>{formatCount(record?.alerts_count)}</Text>
              <Text type="secondary" strong>
                <Private>{formatAmount(record?.alerts_amount)}</Private>
              </Text>
            </Flex>
          )
        case "chargeback_rate": {
          const chargebackRate = record?.chargeback_rate || 0
          const chargebackRateWithoutUs = record?.chargeback_rate_without_us || 0
          return (
            <Flex gap={2} vertical={true} align="flex-end">
              <Text strong>{chargebackRate}%</Text>
              <Text type="secondary" strong>
                {chargebackRateWithoutUs}% {t("without us")}
              </Text>
            </Flex>
          )
        }
        case "prevention_rate":
          return <Text strong>{record?.prevention_rate || 0}%</Text>
        default:
          return null
      }
    },
    [mobileColumn, t]
  )

  return (
    <HealthSubpageLayout backText={t("Back to Health")} isMobile={isMobile}>
      {isMobile ? (
        <Flex vertical gap={12} className="by-country-subpage-mobile__header-block">
          <HealthSubpageHeader title={t("Breakdown by Country")} />
          <DashboardFiltersBlock
            handleChangeFilter={handleChangeFilter}
            filters={filters}
            onlyPeriod={true}
          />
          <DropdownBlockCheckbox
            classNameWrapper="health-dropdown-block-checkbox by-country-subpage-mobile__dropdown"
            id="country"
            withSearch={true}
            setDropdownFilters={handleChangeFilterCountries}
            dropdownFilters={selectCountries}
            allBtnText="All"
            label="Country"
            data={countries}
            newMarkStyle={true}
          />
          <SelectComponent
            size="large"
            value={mobileColumn}
            onChange={setMobileColumn}
            options={mobileColumnOptions}
            style={{ width: "100%" }}
          />
        </Flex>
      ) : (
        <HealthSubpageHeader title={t("Breakdown by Country")}>
          <DropdownBlockCheckbox
            classNameWrapper="health-dropdown-block-checkbox"
            id="country"
            withSearch={true}
            setDropdownFilters={handleChangeFilterCountries}
            dropdownFilters={selectCountries}
            allBtnText="All"
            label="Country"
            data={countries}
            newMarkStyle={true}
          />
          <DashboardFiltersBlock
            handleChangeFilter={handleChangeFilter}
            filters={filters}
            onlyPeriod={true}
          />
        </HealthSubpageHeader>
      )}

      {isMobile ? (
        <div>
          <Flex vertical gap={12} className="by-country-subpage-mobile">
            <div className="by-country-subpage-mobile__header">
              <Text type="secondary">{t("Country")}</Text>
              <Text type="secondary" className="by-country-subpage-mobile__header-right">
                {selectedMobileColumnLabel}
              </Text>
            </div>

            {dataByCountryFetching ? (
              <Flex justify="center" style={{ padding: "24px 0" }}>
                <Spin spinning={true} />
              </Flex>
            ) : result?.length ? (
              result.map((record, index) => {
                const code = record?.country_code?.toUpperCase()
                const image = countryFlagsByCountryCode?.[code] || null
                const isLastRow = index === result.length - 1

                return (
                  <div
                    key={record.country}
                    className={`by-country-subpage-mobile__row${
                      isLastRow ? " by-country-subpage-mobile__row--last" : ""
                    }`}
                  >
                    <Flex gap={8} align="center" className="by-country-subpage-mobile__left">
                      {image && <img src={image} alt={record.country} width={20} height={20} />}
                      <Text strong>{record.country}</Text>
                    </Flex>
                    <div className="by-country-subpage-mobile__right">
                      {renderMobileValueCell(record)}
                    </div>
                  </div>
                )
              })
            ) : (
              <div className="by-country-mobile-table__empty">
                <Empty
                  image={Images.no_data}
                  imageStyle={{ width: 64, height: 40 }}
                  description={t("No Data")}
                />
              </div>
            )}
          </Flex>

          <HealthMobilePagination
            current={pageNumber}
            pageSize={pageSize}
            total={count || 0}
            onChange={setPageNumber}
          />
        </div>
      ) : (
        <TableComponent
          loading={dataByCountryFetching}
          dataSource={result}
          columns={columns}
          scroll={tableSettings.scroll}
          pagination={tableSettings.pagination}
          onChange={handleChangeTable}
          rowKey="country"
          components={tableComponents}
          onRow={(record, index) => ({
            "data-percentage": getRowPercentage(record, result)
          })}
        />
      )}
    </HealthSubpageLayout>
  )
}

const mapStateToProps = (state) => ({
  userInfo: state.auth.userInfo,
  filters: state.health.filters,
  dataByCountryFetching: state.health.dataByCountryFetching,
  dataByCountry: state.health.dataByCountry,
  selectCountries: state.health.selectCountries,
  theme: state.nav.theme,
  admin_selected_business_account_id: state.user.admin_selected_business_account_id,
  isAdmin: state.auth.isAdmin
})

const mapDispatchToProps = (dispatch) => ({
  getDataByCountry: (params) => dispatch(Creators.getDataByCountryRequest(params)),
  changeFilters: (filters) => dispatch(Creators.healthSetProp("filters", filters)),
  changeCounties: (counties) => dispatch(Creators.healthSetProp("selectCountries", counties))
})

export default connect(mapStateToProps, mapDispatchToProps)(BreakdownByCountryPage)
